import { useEffect, useMemo, useState } from 'react';

import { FormattedMessage } from 'react-intl';

import { checkBotCommands } from 'mastodon/utils/bot_commands';
import type {
  BotCommandExpected,
  BotCommandIssue,
} from 'mastodon/utils/bot_commands';
import { useBotCommandsFor } from 'mastodon/utils/bot_commands_store';

import classes from './bot_command_notice.module.scss';

// Checked once typing pauses, so the notice does not flicker on every key
const SETTLE_DELAY = 400;

const Expected: React.FC<{ expected: BotCommandExpected }> = ({ expected }) => {
  switch (expected.kind) {
    case 'keyword':
      return (
        <FormattedMessage
          id='bot_command_notice.expected_keywords'
          defaultMessage='Valid keywords: {names}'
          values={{ names: expected.names.join(', ') }}
        />
      );
    case 'number':
      return expected.range ? (
        <FormattedMessage
          id='bot_command_notice.expected_number'
          defaultMessage='Valid number range: {range}'
          values={{ range: expected.range }}
        />
      ) : (
        <FormattedMessage
          id='bot_command_notice.expected_any_number'
          defaultMessage='Write a whole number'
        />
      );
    case 'dice':
      return expected.range ? (
        <FormattedMessage
          id='bot_command_notice.expected_dice'
          defaultMessage='Valid dice range: {range}'
          values={{ range: expected.range }}
        />
      ) : (
        <FormattedMessage
          id='bot_command_notice.expected_any_dice'
          defaultMessage='Write dice like 2d6'
        />
      );
  }
};

const Issue: React.FC<{ issue: BotCommandIssue }> = ({ issue }) => {
  switch (issue.kind) {
    case 'unknown':
      return (
        <FormattedMessage
          id='bot_command_notice.unknown'
          defaultMessage='Unknown command. Suggestion: {suggestion}'
          values={{
            suggestion: <strong>{issue.suggestion}</strong>,
          }}
        />
      );
    case 'invalid':
      return (
        <>
          {issue.expected.map((expected, index) => (
            // Recomputed whole, in a fixed order
            <span key={index}>
              {index > 0 && ' · '}
              <Expected expected={expected} />
            </span>
          ))}
        </>
      );
    case 'missing':
      return (
        <FormattedMessage
          id='bot_command_notice.missing'
          defaultMessage='Command incomplete. Format: {formats}'
          values={{ formats: <strong>{issue.formats.join(', ')}</strong> }}
        />
      );
    case 'too_many':
      return (
        <FormattedMessage
          id='bot_command_notice.too_many'
          defaultMessage='Keyword error. Format: {formats}'
          values={{ formats: <strong>{issue.formats.join(', ')}</strong> }}
        />
      );
  }
};

/**
 * Under the compose box: what looks wrong in the bot commands written in
 * square brackets (see utils/bot_commands), while the post is being
 * written. It only informs; posting is never held up. Only finished
 * brackets are checked, so nothing shows up halfway through one.
 */
export const BotCommandNotice: React.FC<{ text: string }> = ({ text }) => {
  const [settled, setSettled] = useState(text);

  useEffect(() => {
    // An emptied box (after posting) clears the notice at once
    const timeout = setTimeout(
      () => {
        setSettled(text);
      },
      text ? SETTLE_DELAY : 0,
    );

    return () => {
      clearTimeout(timeout);
    };
  }, [text]);

  const bots = useBotCommandsFor(settled);

  const issues = useMemo(
    () =>
      bots.length > 0 && settled.includes('[')
        ? checkBotCommands(settled, bots)
        : [],
    [settled, bots],
  );

  if (issues.length === 0) return null;

  return (
    <div className={classes.notice} role='status'>
      <ul className={classes.list}>
        {issues.map((issue, index) => (
          // Issues have no identity of their own and are recomputed whole
          <li key={index}>
            <Issue issue={issue} />
          </li>
        ))}
      </ul>
    </div>
  );
};
